import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Button from 'components/atoms/Button/Button';
import Heading from 'components/atoms/Heading/Heading';
import LoadingScreen from 'components/atoms/LoadingScreen/LoadingScreen';
import Txt from 'components/atoms/Txt/Txt';
import { ConversionEvent } from 'components/templates/ThirdPartyTrackingProvider/trackingScriptEvents';
import { useOnConversionEvent } from 'components/templates/ThirdPartyTrackingProvider/useConversionEvent';
import useQueryStringParams from 'hooks/useQueryStringParams';
import { useRouter } from 'next/router';
import React, { ForwardedRef, RefObject, useRef } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { SurveyFinishQuery } from './__generated__/SurveyFinishQuery.graphql';
import { TERMINATE_MESSAGES } from './SurveyEndMessages';
import classes from './SurveyFinish.module.scss';

export interface Props {}

interface SurveyFinishData {
  reward?: number;
  status: string;
}
interface SurveyFinishStatusProps {
  result: SurveyFinishData;
  firstName?: string | null;
}

const CompleteScreen = React.forwardRef<HTMLElement, SurveyFinishStatusProps>(
  function CompleteScreen(
    { result, firstName }: SurveyFinishStatusProps,
    ref: ForwardedRef<HTMLElement> | null,
  ) {
    useOnConversionEvent(ConversionEvent.SURVEY_COMPLETE);
    return (
      <>
        <div className={classes.screenContainer} ref={ref as RefObject<HTMLDivElement>}>
          {firstName && (
            <Txt className={classes.nameIntro} component="p">
              Thanks, {{ firstName }}
            </Txt>
          )}
          <Heading className={classes.title} level={2}>
            You&apos;ve completed this survey. Thanks for sharing your opinions!
          </Heading>
          {result.reward && result.reward > 0 && (
            <div className={classes.reward}>
              <Txt className={classes.rewardTitle} component="strong">
                You&apos;ve earned
              </Txt>
              <Txt className={classes.points} component="span" skipTranslation>
                <span className={classes.currencySymbol}>$</span>
                {(result.reward / 100).toFixed(2)}
              </Txt>
              <Txt component="p" className={classes.lowerText}>
                Fancy another round?
              </Txt>
            </div>
          )}
          <Button
            buttonSize="large"
            component={A}
            href="/dashboard"
            buttonType="primary"
            className={clsx(classes.ctaButton)}
          >
            <Txt>Show me more surveys</Txt>
          </Button>
        </div>
      </>
    );
  },
);

const TerminateScreen = React.forwardRef<HTMLElement, SurveyFinishStatusProps>(
  function TerminateScreen(
    { result, firstName }: SurveyFinishStatusProps,
    ref: ForwardedRef<HTMLElement> | null,
  ) {
    const { title, description } =
      TERMINATE_MESSAGES[result.status] || TERMINATE_MESSAGES.terminate;
    return (
      <div className={classes.screenContainer} ref={ref as RefObject<HTMLDivElement>}>
        {firstName && (
          <Txt className={classes.nameIntro} component="p">
            Hey, {{ firstName }}
          </Txt>
        )}
        <Heading className={classes.title} level={2}>
          {title}
        </Heading>
        <Txt className={classes.description}>{description}</Txt>
        <Button
          buttonSize="large"
          buttonType="primary"
          component={A}
          href="/dashboard"
          className={clsx(classes.ctaButton)}
        >
          <Txt>Find more surveys</Txt>
        </Button>
      </div>
    );
  },
);

const SurveyFinish: React.FC<Props> = () => {
  const router = useRouter();
  const screenRef = useRef<HTMLDivElement>(null);
  const { status, reward, study_id } = useQueryStringParams();

  const data = useLazyLoadQuery<SurveyFinishQuery>(
    graphql`
      query SurveyFinishQuery {
        userData {
          firstName
        }
      }
    `,
    {},
  );

  if (!router.isReady) {
    return <LoadingScreen />;
  } else if (status === 'complete' && study_id === 'profiler') {
    router.push('/dashboard?showPushNotificationModal=true', '/dashboard');
  }

  const resultData = {
    reward: reward ? Number(reward) : undefined,
    status,
  };

  function renderContent() {
    if (status === 'complete') {
      return (
        <CompleteScreen result={resultData} ref={screenRef} firstName={data.userData?.firstName} />
      );
    } else {
      return (
        <TerminateScreen result={resultData} ref={screenRef} firstName={data.userData?.firstName} />
      );
    }
  }
  return <div className={clsx(classes.root)}>{renderContent()}</div>;
};

export default SurveyFinish;
